import { Bucket } from './types';

/**
 * Count-to-level mapping for the heatmap.
 *
 * Two scales, deliberately different:
 *
 * - Active days is absolute, scaled against a per-bucket denominator (see
 *   DAYS_PER_BUCKET below), so a cell means the same thing in every row of
 *   the table for a given bucket grain.
 * - Metric bands are relative to the band's own maximum, because slide views
 *   and lessons completed have incomparable magnitudes. A dark cell in a band
 *   means "heavy for this metric", not "heavy overall".
 *
 * Level 0 is reserved for genuine zero and is rendered in a warm hue rather
 * than a pale tint of the ramp, so gaps read as gaps rather than as low
 * values. That distinction is the whole point of the report.
 */

export const MAX_LEVEL = 5;
export const ZERO_LEVEL = 0;

/**
 * Active-day denominators per bucket. These are deliberately FIXED rather than
 * computed from the calendar: a constant denominator keeps the colour scale
 * uniform across columns, so two months can be compared at a glance. Real
 * school days per month vary (roughly 18-23) and the client cannot know a
 * district's holidays anyway, so 20 is a stated convention, not a measurement.
 */
export const DAYS_PER_BUCKET: Record<Bucket, number> = { week: 5, month: 20 };

function safeCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value;
}

/** A row's total for the Total column. Bad values count as zero, as on the colour scale. */
export function sumCounts(counts: number[]): number {
  return counts.reduce((acc, value) => acc + safeCount(value), 0);
}

/**
 * Resolves the active-day denominator for a bucket, honouring an optional
 * admin override (e.g. a district that only expects two active days a
 * week). The override is purely presentational -- it lives in `UrlState`,
 * not `ReportQuery`, so changing it never refetches the report -- and is
 * defended here rather than trusting callers: non-finite, zero, or negative
 * values fall back to the bucket default instead of producing a broken or
 * inverted scale.
 */
export function resolveActiveDayTarget(bucket: Bucket, override?: number | null): number {
  if (override && Number.isFinite(override) && override > 0) return Math.floor(override);
  return DAYS_PER_BUCKET[bucket] ?? DAYS_PER_BUCKET.week;
}

export function activeDayLevel(
  count: number,
  bucket: Bucket = 'week',
  activeDayTargetOverride?: number | null,
): number {
  const safe = safeCount(count);
  if (safe === 0) return ZERO_LEVEL;
  const denominator = resolveActiveDayTarget(bucket, activeDayTargetOverride);
  const ratio = Math.min(1, safe / denominator);
  // Any non-zero count must be visibly non-empty, hence the floor of 1.
  return Math.max(1, Math.ceil(ratio * MAX_LEVEL));
}

export function bandLevels(counts: number[]): number[] {
  const max = counts.reduce((acc, value) => Math.max(acc, safeCount(value)), 0);
  if (max === 0) return counts.map(() => ZERO_LEVEL);

  return counts.map((value) => {
    const safe = safeCount(value);
    if (safe === 0) return ZERO_LEVEL;
    // Any non-zero count must be visibly non-empty, so floor at level 1.
    return Math.max(1, Math.ceil((safe / max) * MAX_LEVEL));
  });
}

interface DescribeCellArgs {
  entityName: string;
  bucketLabel: string;
  count: number;
  kind: 'activeDays' | 'metric';
  metricLabel?: string;
  bucket?: Bucket;
  /**
   * Admin override for the active-day denominator. Only meaningful for
   * `kind: 'activeDays'` -- metric-band cells describe raw event counts, not
   * a ratio against an expected number of active days -- but accepted
   * unconditionally so every call site can pass it the same way regardless
   * of cell kind.
   */
  activeDayTarget?: number | null;
}

export function describeCell({
  entityName,
  bucketLabel,
  count,
  kind,
  metricLabel,
  bucket = 'week',
  activeDayTarget,
}: DescribeCellArgs): string {
  if (kind === 'activeDays') {
    if (count <= 0) {
      return `${entityName}, ${bucketLabel}, no activity.`;
    }
    const denominator = resolveActiveDayTarget(bucket, activeDayTarget);
    return `${entityName}, ${bucketLabel}, ${count} of ${denominator} active days.`;
  }
  return `${entityName}, ${bucketLabel}, ${metricLabel}: ${count}.`;
}
